import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GenericResModel } from 'countable@model';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FIRM_STATUS, PlaidDeactivatedBy, PlaidEngagementDetails, PlaidInstitution, PlaidStatus, PlaidUnlinkAccount, PlaidUser, PlaidUserStatus } from '../model/plaid.model';

@Injectable({
  providedIn: "root",
})
export class PlaidService {
  plaidTermsShow$: Subject<string> = new Subject<string>();
  isTermsAndCondition$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private httpClient: HttpClient) {}

  sendEmailToPlaid(engagementID: any, clientId: number): Observable<GenericResModel<string>> {
    return this.httpClient.post<GenericResModel<string>>(environment.apiV1 + "/plaid/token", {
      'clientId': clientId,
      'engagementId': engagementID,
      'type': 'link'
    });
  }

  saveToken(clientKey: string, public_token: string) {
    return this.httpClient.post<GenericResModel<string>>(environment.apiV1 + "/plaid/client/save-token", {
      'public_token': public_token,
      'clientKey': clientKey,
      'type': 'save'
    });
  }

  fetchAccounts(clientKey: string): Observable<GenericResModel<PlaidInstitution[]>> {
    return this.httpClient.get<GenericResModel<PlaidInstitution[]>>(
      environment.apiV1 + "/plaid/accounts?clientKey=" + clientKey
    );
  }

  fetchAccountsClientSide(clientKey: string): Observable<GenericResModel<PlaidInstitution[]>> {
    return this.httpClient.get<GenericResModel<PlaidInstitution[]>>(
      environment.apiV1 + "/plaid/client/accounts?clientKey=" + clientKey
    );
  }

  plaidStatus(clientId: number, engagementId: number): Observable<GenericResModel<PlaidStatus>> {
    return this.httpClient.get<GenericResModel<PlaidStatus>>(
      environment.apiV1 + "/plaid/status?clientId=" + clientId + "&engagementId=" + engagementId
    );
  }

  sendPlaidEmail(data): Observable<GenericResModel<boolean>> {
    return this.httpClient.post<GenericResModel<boolean>>(environment.apiV1 + "/plaid/request", data);
  }

  getClientDetails(clientKey: string): Observable<GenericResModel<PlaidEngagementDetails>> {
    return this.httpClient.get<GenericResModel<PlaidEngagementDetails>>(
      environment.apiV1 + "/plaid/detail?clientKey=" + clientKey
    );
  }

  getClientSideDetails(clientKey: string): Observable<GenericResModel<PlaidEngagementDetails>> {
    return this.httpClient.get<GenericResModel<PlaidEngagementDetails>>(
      environment.apiV1 + "/plaid/client/detail?clientKey=" + clientKey
    );
  }

  linkBankingClientSide(clientKey: string): Observable<GenericResModel<string>> {
    return this.httpClient.post<GenericResModel<string>>(environment.apiV1 + "/plaid/client/token", {
      'clientKey': clientKey,
      'type': 'link'
    });
  }

  firmStatus(): Observable<GenericResModel<FIRM_STATUS>> {
    return this.httpClient.get<GenericResModel<FIRM_STATUS>>(environment.apiV2 + "/firm/plaid-status");
  }

  registerFirm(data: any): Observable<GenericResModel<boolean>> {
    return this.httpClient.post<GenericResModel<boolean>>(environment.apiV1 + "/plaid/register-firm", data);
  }

  unRegisterFirm(data: any): Observable<GenericResModel<boolean>> {
    return this.httpClient.post<GenericResModel<boolean>>(environment.apiV1 + "/plaid/unregister-firm", data);
  }

  getBankLogo(id: any): Observable<GenericResModel<string>> {
    return this.httpClient.post<GenericResModel<string>>(environment.apiV1 + "/plaid/institution-logo", id);
  }

  plaidUnlinkAccount(data: PlaidUnlinkAccount): Observable<GenericResModel<boolean>> {
    return this.httpClient.post<GenericResModel<boolean>>(environment.apiV1 + "/plaid/unlink-account", data);
  }

  plaidDisconnectEng(data: any): Observable<GenericResModel<boolean>> {
    return this.httpClient.post<GenericResModel<boolean>>(environment.apiV1 + "/plaid/disconnect-eng", data);
  }

  getBankLogoClientSite(id: any): Observable<GenericResModel<string>> {
    return this.httpClient.post<GenericResModel<string>>(environment.apiV1 + "/plaid/client/institution-logo", id);
  }

  plaidUnlinkAccountClientSite(data: PlaidUnlinkAccount): Observable<GenericResModel<boolean>> {
    return this.httpClient.post<GenericResModel<boolean>>(environment.apiV1 + "/plaid/client/unlink-account", data);
  }

  plaidDisconnectEngClientSite(data: any): Observable<GenericResModel<boolean>> {
    return this.httpClient.post<GenericResModel<boolean>>(environment.apiV1 + "/plaid/client/disconnect-eng", data);
  }

  plaidInstructions(): Observable<GenericResModel<boolean>> {
    return this.httpClient.post<GenericResModel<boolean>>(environment.apiV1 + "/plaid/sign", {});
  }

  plaidAccessList(): Observable<GenericResModel<PlaidUser[]>> {
    return this.httpClient.get<GenericResModel<PlaidUser[]>>(environment.apiV1 + "/plaid/members");
  }

  changePlaidAccess(users: PlaidUser[]): Observable<GenericResModel<PlaidUser[]>> {
    return this.httpClient.post<GenericResModel<PlaidUser[]>>(environment.apiV1 + "/plaid/members", users);
  }

  deactivateAcknowledgePlaid(): Observable<GenericResModel<boolean>> {
    return this.httpClient.post<GenericResModel<boolean>>(environment.apiV1 + "/plaid/unregister-user-ack", {});
  }

  plaidDeactivatedBy(): Observable<GenericResModel<PlaidDeactivatedBy>> {
    return this.httpClient.get<GenericResModel<PlaidDeactivatedBy>>(environment.apiV1 + "/plaid/deactivated-by");
  }

  getUserPlaidStatus(): Observable<GenericResModel<PlaidUserStatus>> {
    return this.httpClient.get<GenericResModel<PlaidUserStatus>>(environment.apiV1 + "/plaid/user-detail");
  }
}
